/** Aligned uniform buffer write helper. WebGPU requires uniform offsets aligned to 256. */

export function createUniformBuffer(device: GPUDevice, sizeBytes: number, label?: string): GPUBuffer {
  return device.createBuffer({
    label: label ?? 'uniform',
    size: Math.ceil(sizeBytes / 16) * 16,
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
  });
}

export function writeUniform(device: GPUDevice, buffer: GPUBuffer, data: Float32Array | Uint32Array, offset = 0): void {
  device.queue.writeBuffer(buffer, offset, data.buffer, data.byteOffset, data.byteLength);
}

export function createStorageBuffer(device: GPUDevice, sizeBytes: number, opts?: { label?: string; usageExtra?: GPUBufferUsageFlags }): GPUBuffer {
  return device.createBuffer({
    label: opts?.label ?? 'storage',
    size: sizeBytes,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST | GPUBufferUsage.COPY_SRC | (opts?.usageExtra ?? 0),
  });
}

/**
 * Empty bind-group + layout pair for satisfying contiguous group(N) indices when
 * a pipeline doesn't actually bind anything at that slot. Use the same returned
 * layout both at pipelineLayout construction and pass.setBindGroup time.
 */
export function createEmptyBindGroup(device: GPUDevice, label: string): {
  layout: GPUBindGroupLayout;
  bindGroup: GPUBindGroup;
} {
  const layout = device.createBindGroupLayout({ label: `${label}.empty.bgl`, entries: [] });
  const bindGroup = device.createBindGroup({ label: `${label}.empty.bg`, layout, entries: [] });
  return { layout, bindGroup };
}

/** Storage texture for compute outputs that are also sampled later. */
export function createStorageTexture2D(
  device: GPUDevice,
  size: readonly [number, number],
  format: GPUTextureFormat,
  label?: string,
): GPUTexture {
  return device.createTexture({
    label: label ?? 'storageTex',
    size: { width: size[0], height: size[1] },
    format,
    usage:
      GPUTextureUsage.STORAGE_BINDING |
      GPUTextureUsage.TEXTURE_BINDING |
      GPUTextureUsage.COPY_SRC |
      GPUTextureUsage.COPY_DST,
  });
}
